import { installCrashReporting } from './core/telemetry/report';
import { installDiagnostics } from './core/telemetry/diag';
import { startSession, endSessionCleanly } from './core/telemetry/session';
import { sendCutStops } from './core/telemetry/stopLog';
import { recordInstallToday } from './features/install/InstallGate';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './app/App';
import { initTextSize } from './platform/textSize';
import { initLang } from './platform/lang';
import './styles.css';

/*
 * Take the new version, and actually SHOW it.
 *
 * The service worker already calls skipWaiting and clients.claim, so a new
 * build takes control immediately — but the page on screen was loaded from the
 * OLD cache and nothing ever told it to fetch again. On an installed
 * home-screen app, which is rarely torn down, a driver could keep running a
 * build from days earlier: the owner reported opening the app and seeing the
 * summary screen from a previous delivery, and several deploys never reached
 * his phone at all.
 *
 * `controllerchange` fires the moment a new worker takes over. Reloading there
 * is the difference between shipping a fix and believing we shipped one.
 *
 * The guard matters: without it, a reload can trigger another controllerchange
 * and loop the app forever, which is worse than a stale build.
 */
/*
 * LOOK FOR A NEW VERSION WHENEVER THE APP COMES BACK ON SCREEN (5 Oct).
 *
 * The browser only checks for a new version when the app is loaded from
 * scratch. An installed app is mostly brought back from the background, not
 * reloaded -- on 5 Oct about sixteen drivers were still on a build from the
 * morning of 4 Oct, a day and a half and three fixes later. Now it checks each
 * time it returns to the screen and every 30 minutes, but never while a
 * driver is inside a stop.
 */
const inStop = () => window.location.pathname.startsWith('/stop');
registerSW({
  immediate: true,
  onRegisteredSW(_url, reg) {
    if (!reg) return;
    const check = () => {
      if (document.visibilityState !== 'visible' || inStop()) return;
      void reg.update().catch(() => { /* offline: next time */ });
    };
    document.addEventListener('visibilitychange', check);
    setInterval(check, 30 * 60 * 1000);
  },
});

/*
 * ── THE GUARD HAS TO SURVIVE THE RELOAD ────────────────────────────────────
 *
 * It was a plain `let reloading = false`, which dies with the very page it is
 * protecting: reload, fresh module, guard back to false, the worker claims the
 * new page, controllerchange again, reload again. Forever.
 *
 * On 23 Sep that took down both test phones for an hour — six identical
 * requests a second, the route never rendering, and nothing in the error
 * reporter because nothing threw. It looked exactly like "the app cannot see
 * my route".
 *
 * sessionStorage is the right lifetime: it outlives a reload, so the loop
 * cannot start, and it is cleared when the app is genuinely closed, so the
 * next launch can still take a new build once.
 */
const RELOADED_KEY = 'jw.swReloaded';

if ('serviceWorker' in navigator) {
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;

    // Already reloaded once for a worker change in this session: take the new
    // build on the next natural launch instead of fighting the page.
    try {
      if (sessionStorage.getItem(RELOADED_KEY) === '1') return;
      sessionStorage.setItem(RELOADED_KEY, '1');
    } catch {
      // Storage unavailable (private mode, blocked). Reloading blind is how
      // the loop happened, so do nothing and let the next launch pick it up.
      return;
    }

    reloading = true;
    // Never reload in the middle of a stop: wait until the driver is out of it.
    const go = () => {
      if (inStop()) { setTimeout(go, 3000); return; }
      /*
       * Say goodbye before going. Without this the update reads as a crash on
       * the next launch, and a deploy produces a burst of false 'died' reports
       * across the whole fleet at once.
       */
      endSessionCleanly();
      window.location.reload();
    };
    go();
  });
}

// Before React renders: a driver who chose large text should never see a frame
// of small text first.
initTextSize();
initLang();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Nothing else knows when the app breaks. This does.
installCrashReporting();
/*
 * Must come AFTER installCrashReporting: starting the session is what reports
 * how the previous one ended, and that report needs the reporter running.
 *
 * Wrapped, and this is not defensive habit. This runs BEFORE React mounts, so
 * anything thrown here is a white screen for every driver in the fleet — and it
 * touches localStorage, which throws outright in private mode and on a full
 * disk. Diagnostics are worth a great deal; they are not worth the app.
 */
try {
  startSession();
} catch (err) {
  console.error('[session] could not start', err);
}
/* Storage room, waiting work, time in the background. Never throws. */
installDiagnostics();
/*
 * Before React: a stop screen restored on reload must not have its fresh
 * record sent as 'cut'. And home-screen or tab, once a day per phone (5 Oct).
 */
try { sendCutStops(); recordInstallToday(); } catch { /* diagnostics never cost the app */ }
